import * as React from 'react';
import { Wavelength } from 'js-electrical-engineering-equations';
import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { useLastCalculated } from '../../hooks/useLastCalculated';

const fieldsDefaults = {
  frequency: '146',
  threeQuartersWavelength: '',
  fiveEighthsWavelength: '',
  halfWavelength: '',
  quarterWavelength: '',
};

export function WavelengthComponent(): JSX.Element {
  const [fields, setFields] = useState(fieldsDefaults);
  const [wavelength, setWavelength] = useState(new Wavelength(146));

  const { lastCalculated, wasLastCalculated, setLastCalculated } = useLastCalculated<
    keyof typeof fieldsDefaults
  >();

  const clear = useCallback(() => {
    setLastCalculated([]);
    setFields(fieldsDefaults);
    setWavelength(new Wavelength(parseFloat(fieldsDefaults.frequency)));
  }, [setLastCalculated, setFields]);

  const onChange = useCallback(
    (event) => {
      const field = event.target.name;
      const value = event.target.value;
      setFields({ ...fields, [field]: value });
    },
    [fields, setFields],
  );

  const calculate = useCallback(
    (e) => {
      e.preventDefault();
      setWavelength(new Wavelength(parseFloat(fields.frequency)));
    },
    [fields, lastCalculated, wasLastCalculated],
  );

  return (
    <form onSubmit={calculate}>
      <div>
        <Card>
          <CardContent>
            <div>
              <p>Supply a frequency to calculate the wavelength.</p>
              <TextField
                label="Frequency in MHz"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                name="frequency"
                margin="normal"
                onChange={onChange}
                value={fields.frequency}
              />
            </div>
          </CardContent>
          <CardActions>
            <Button type="button" tabIndex="6" onClick={clear}>
              Clear
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              style={{ marginLeft: 'auto' }}
            >
              Calculate
            </Button>
          </CardActions>
        </Card>
        <Card>
          <CardContent>
            <div>
              <Table className="table" width="100%">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Meters</TableCell>
                    <TableCell>Feet</TableCell>
                    <TableCell>Inches</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Full Wavelength</TableCell>
                    <TableCell>{wavelength.toString()}</TableCell>
                    <TableCell>{wavelength.toFeet()}</TableCell>
                    <TableCell>{wavelength.toInches()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>3/4 Wavelength</TableCell>
                    <TableCell>{wavelength.toThreeQuartersWavelength('m')}</TableCell>
                    <TableCell>{wavelength.toThreeQuartersWavelength('f')}</TableCell>
                    <TableCell>{wavelength.toThreeQuartersWavelength('in')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>5/8 Wavelength</TableCell>
                    <TableCell>{wavelength.toFiveEighthsWavelength('m')}</TableCell>
                    <TableCell>{wavelength.toFiveEighthsWavelength('f')}</TableCell>
                    <TableCell>{wavelength.toFiveEighthsWavelength('in')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1/2 Wavelength</TableCell>
                    <TableCell>{wavelength.toHalfWavelength('m')}</TableCell>
                    <TableCell>{wavelength.toHalfWavelength('f')}</TableCell>
                    <TableCell>{wavelength.toHalfWavelength('in')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>1/4 Wavelength</TableCell>
                    <TableCell>{wavelength.toQuarterWavelength('m')}</TableCell>
                    <TableCell>{wavelength.toQuarterWavelength('f')}</TableCell>
                    <TableCell>{wavelength.toQuarterWavelength('in')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </form>
  );
}
